import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, X, Plus } from 'lucide-react';
import { useComparison } from '../contexts/ComparisonContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const resolveImg = (url) => url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const VehicleComparison = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedVehicles, removeVehicle, clearAll } = useComparison();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if we have vehicle IDs from URL params (direct link)
    const vehicleIds = searchParams.get('ids')?.split(',') || [];
    
    if (vehicleIds.length > 0 && vehicleIds[0]) {
      fetchVehiclesById(vehicleIds);
    } else if (selectedVehicles.length > 0) {
      // Use vehicles from context
      setVehicles(selectedVehicles);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [searchParams, selectedVehicles]);

  const fetchVehiclesById = async (ids) => {
    try {
      const response = await axios.post(`${API}/vehicles/compare`, ids);
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveVehicle = (vehicleId) => {
    removeVehicle(vehicleId);
    setVehicles(vehicles.filter(v => v.vehicle_id !== vehicleId));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div className="min-h-screen pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <h1 className="text-4xl font-heading font-bold mb-4">No Vehicles Selected</h1>
            <p className="text-xl text-textSecondary mb-8">
              Start comparing vehicles by selecting "Add to Compare" on vehicle cards
            </p>
            <Link 
              to="/marketplace" 
              className="inline-block bg-primary hover:bg-primaryHover text-white font-bold px-8 py-3 rounded-sm transition-colors"
            >
              Browse Vehicles
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Comparison fields with the user's requested details
  const comparisonFields = [
    { key: 'price', label: 'Price', format: (v, vehicle) => {
      if (vehicle.listing_type === 'direct_sale') {
        return v ? `$${v.toLocaleString()}` : 'N/A';
      } else {
        // For auctions, show starting price or current bid
        return vehicle.starting_price ? `$${vehicle.starting_price.toLocaleString()} (starting)` : 'Auction';
      }
    }},
    { key: 'listing_type', label: 'Listing Type', format: (v) => v === 'direct_sale' ? 'Direct Sale' : 'Auction' },
    { key: 'year', label: 'Year' },
    { key: 'mileage', label: 'Mileage', format: (v) => `${v?.toLocaleString() || 'N/A'} mi` },
    { key: 'condition', label: 'Condition', format: (v) => v?.toUpperCase() || 'N/A' },
    { key: 'damage_type', label: 'Damage Type', format: (v) => v || 'None' },
    { key: 'body_type', label: 'Body Type', format: (v) => v ? v.charAt(0).toUpperCase() + v.slice(1) : 'N/A' },
    { key: 'transmission', label: 'Transmission', format: (v) => v || 'N/A' },
    { key: 'fuel_type', label: 'Fuel Type', format: (v) => v || 'N/A' },
    { key: 'drivetrain', label: 'Drivetrain', format: (v) => v || 'N/A' },
    { key: 'engine_size', label: 'Engine Size', format: (v) => v || 'N/A' },
    { key: 'color', label: 'Exterior Color', format: (v) => v || 'N/A' },
    { key: 'location', label: 'Location' },
    { key: 'vin', label: 'VIN', format: (v) => v || 'N/A' },
  ];

  // Pad with empty slots to show up to 3 columns
  const displayVehicles = [...vehicles];
  while (displayVehicles.length < 3) {
    displayVehicles.push(null);
  }

  return (
    <div className="min-h-screen pt-20 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link 
              to="/marketplace" 
              className="flex items-center space-x-2 text-textSecondary hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Marketplace</span>
            </Link>
          </div>
          {vehicles.length > 0 && (
            <button
              onClick={() => {
                clearAll();
                navigate('/marketplace');
              }}
              className="text-textSecondary hover:text-white transition-colors"
            >
              Clear All
            </button>
          )}
        </div>

        <h1 className="text-4xl font-heading font-bold mb-8">
          Vehicle Comparison ({vehicles.length}/3)
        </h1>

        {/* Comparison Table */}
        <div className="glass rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="p-6 text-left font-heading text-lg w-48 sticky left-0 bg-surface z-10">
                    Specification
                  </th>
                  {displayVehicles.map((vehicle, idx) => (
                    <th key={idx} className="p-6 text-left min-w-[300px]">
                      {vehicle ? (
                        <div className="relative">
                          {/* Remove Button */}
                          <button
                            onClick={() => handleRemoveVehicle(vehicle.vehicle_id)}
                            className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 rounded-full p-2 transition-colors z-20"
                            title="Remove from comparison"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          
                          {/* Vehicle Card */}
                          <Link to={`/vehicle/${vehicle.vehicle_id}`} className="block group">
                            <img
                              src={resolveImg(vehicle.images?.[0])}
                              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                              className="w-full aspect-video object-cover rounded-sm mb-4 group-hover:opacity-80 transition-opacity"
                            />
                            <h3 className="font-heading font-bold text-xl mb-2 group-hover:text-primary transition-colors">
                              {vehicle.year} {vehicle.make} {vehicle.model}
                            </h3>
                            
                            {/* Price Display */}
                            {vehicle.listing_type === 'direct_sale' ? (
                              <div className="text-2xl font-heading font-bold text-primary">
                                {vehicle.currency === 'NGN' ? '₦' : '$'}{vehicle.price?.toLocaleString()}
                              </div>
                            ) : (
                              <div className="text-lg font-heading font-bold text-primary">
                                {vehicle.starting_price 
                                  ? `Starting: $${vehicle.starting_price.toLocaleString()}`
                                  : 'Auction Vehicle'}
                              </div>
                            )}
                            
                            {/* Listing Type Badge */}
                            <div className="mt-2">
                              <span className={`inline-block px-3 py-1 rounded-sm text-xs font-bold uppercase ${
                                vehicle.listing_type === 'direct_sale' ? 'bg-blue-600' : 'bg-primary'
                              }`}>
                                {vehicle.listing_type === 'direct_sale' ? 'For Sale' : 'Auction'}
                              </span>
                            </div>
                          </Link>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-white/10 rounded-sm">
                          <Plus className="w-12 h-12 text-textSecondary mb-2" />
                          <p className="text-textSecondary text-sm">Add Vehicle</p>
                          <Link 
                            to="/marketplace" 
                            className="mt-2 text-primary hover:underline text-sm"
                          >
                            Browse Marketplace
                          </Link>
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonFields.map((field, idx) => (
                  <tr 
                    key={idx} 
                    className={`border-b border-white/10 hover:bg-surfaceHighlight transition-colors ${
                      idx % 2 === 0 ? 'bg-surface' : 'bg-surfaceHighlight/30'
                    }`}
                  >
                    <td className="p-6 font-medium text-textSecondary sticky left-0 bg-inherit z-10">
                      {field.label}
                    </td>
                    {displayVehicles.map((vehicle, vIdx) => (
                      <td key={vIdx} className="p-6">
                        {vehicle ? (
                          field.format
                            ? field.format(vehicle[field.key], vehicle)
                            : vehicle[field.key] || '-'
                        ) : (
                          <span className="text-textSecondary">-</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Action Row */}
                <tr className="bg-surface">
                  <td className="p-6 font-medium text-textSecondary sticky left-0 bg-surface z-10">
                    Actions
                  </td>
                  {displayVehicles.map((vehicle, vIdx) => (
                    <td key={vIdx} className="p-6">
                      {vehicle && (
                        <Link
                          to={`/vehicle/${vehicle.vehicle_id}`}
                          className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-3 inline-block transition-colors"
                        >
                          View Full Details
                        </Link>
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-8 text-center text-textSecondary text-sm">
          <p>Select up to 3 vehicles to compare side-by-side. Click "Add to Compare" on any vehicle card in the marketplace.</p>
        </div>
      </div>
    </div>
  );
};

export default VehicleComparison;
